// src/components/Register/Register.js

import React, { useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import { auth, googleProvider } from "../../firebaseConfig";
import { createUserWithEmailAndPassword, signInWithPopup, updateProfile } from "firebase/auth";
import { useAuthRedirect } from "../../hooks/useAuthRedirect";
import { Link } from "react-router-dom"; // Ensure Link is imported
import "react-toastify/ReactToastify.css";
import "./Register.css";

const PROJECT_KEY = "ijrr_user";

export default function Register({ onLoginSuccess }) {
  const { redirectAuthenticatedUser } = useAuthRedirect();
  
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [termsAgreed, setTermsAgreed] = useState(false);
  
  // New state to handle the specific "User Exists" case
  const [userAlreadyExists, setUserAlreadyExists] = useState(false);

  const clearForm = () => {
    setFirstName("");
    setLastName("");
    setMobile("");
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setTermsAgreed(false);
    setUserAlreadyExists(false);
  };

  const redirectUser = (userData) => {
    localStorage.setItem(PROJECT_KEY, JSON.stringify(userData));
    console.log("Registration successful", userData);
    toast.success("User registered successfully. Redirecting...", { autoClose: 1000 });
    
    setTimeout(() => {
      // FIX: Pass 'register' action to force redirection to /customize-membership
      redirectAuthenticatedUser(userData, 'register');
    }, 1500);
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setUserAlreadyExists(false);

    if (password !== confirmPassword) {
      toast.error("The passwords you entered do not match.", { autoClose: 2000 });
      return;
    }
    if (!termsAgreed) {
      toast.error("You must agree to the Privacy Policy to proceed.", { autoClose: 2000 });
      return;
    }

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      await updateProfile(user, { displayName: `${firstName} ${lastName}` });

      const userData = {
        firebase_uid: user.uid,
        first_name: firstName,
        last_name: lastName,
        mobile: mobile,
        email: user.email,
        sign_up_method: "email_pass",
        status: "active",
        created_at_firebase: new Date(user.metadata.creationTime).toISOString(),
        last_login_at_firebase: new Date(user.metadata.lastSignInTime).toISOString(),
      };

      await registerUser(userData);

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/customers/${user.uid}`);
      if (response.ok) {
        const data = await response.json();
        userData.profile_completed_at = data.customer.profile_completed_at;
      }
      redirectUser(userData);
      clearForm();

    } catch (error) {
      console.error("Registration error:", error.code, error.message);
      
      if (error.code === 'auth/email-already-in-use') {
        setUserAlreadyExists(true);
        toast.info("This email address is already associated with an account.", { autoClose: 3000 });
      } else if (error.code === 'auth/weak-password') {
        toast.error("The password is too weak. Please use at least 6 characters.", { autoClose: 3000 });
      } else {
        toast.error("We could not complete your registration. Please try again later.", { autoClose: 2000 });
      }
    }
  };

  const handleGoogleSignIn = async () => {
    setUserAlreadyExists(false);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      const names = user.displayName ? user.displayName.split(" ") : ["", ""];
      const userData = {
        firebase_uid: user.uid,
        first_name: names[0],
        last_name: names.slice(1).join(" "),
        email: user.email,
        avatar: user.photoURL,
        sign_up_method: "google",
        status: "active",
        created_at_firebase: new Date(user.metadata.creationTime).toISOString(),
        last_login_at_firebase: new Date(user.metadata.lastSignInTime).toISOString(),
      };
      await registerUser(userData);
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/customers/${user.uid}`);
      if (response.ok) {
        const data = await response.json();
        userData.profile_completed_at = data.customer.profile_completed_at;
      }
      redirectUser(userData);
    } catch (error) {
      console.error("Google sign-in error:", error);
      toast.error("Google sign-in failed. Please try again.", { autoClose: 1000 });
    }
  };

  const registerUser = async (userData) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/customers`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(userData),
      });
      if (!response.ok) {
        const errorData = await response.json();
        console.error("Error response data:", errorData);
        toast.error("Failed to save user data. Please contact support.", { autoClose: 1000 });
      } else {
        console.log("User registered successfully");
      }
    } catch (error) {
      console.error("Failed to register user:", error);
      toast.error("Error connecting to the server.", { autoClose: 1000 });
    }
  };

  return (
    <section className="register-page">
      <ToastContainer />
      <div className="register-container">
        <div className="codex-authmain">
          <form className="codex-authbox" onSubmit={handleSignUp}>
            <div className="group-form text-center">
              <h3>Create Your Account</h3>
              <h6>
                Already have an account?{" "}
                <Link className="text-primary text-decoration-underline" to="/login">
                  Login here
                </Link>
              </h6>
            </div>

            <div className="group-form auth-two-columns">
              <div>
                <label className="form-label">First Name</label>
                <input className="form-control" type="text" placeholder="Edward" value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
              </div>
              <div>
                <label className="form-label">Last Name</label>
                <input className="form-control" type="text" placeholder="Teach" value={lastName} onChange={(e) => setLastName(e.target.value)} required />
              </div>
            </div>

            <div className="group-form">
              <label className="form-label">Mobile</label>
              <input className="form-control" type="tel" placeholder="+1-555-000-0000" value={mobile} onChange={(e) => setMobile(e.target.value)} required />
            </div>

            <div className="group-form">
              <label className="form-label">Email</label>
              <input className="form-control" type="email" placeholder="mail@example.com" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>

            <div className="group-form auth-two-columns">
              <div>
                <label className="form-label">Password</label>
                <input className="form-control" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
              </div>
              <div>
                <label className="form-label">Confirm Password</label>
                <input className="form-control" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required />
              </div>
            </div>

            <div className="group-form">
              <label className="auth-checkbox">
                <input 
                  type="checkbox" 
                  checked={termsAgreed} 
                  onChange={(e) => setTermsAgreed(e.target.checked)} 
                  className="register-chekcbox" 
                />
                I agree to the <Link to="/privacy-policy" className="text-decoration-underline ms-1">Privacy Policy</Link>
              </label>
            </div>

            {/* ERROR ALERT BLOCK */}
            {userAlreadyExists && (
              <div className="alert alert-warning text-center mt-3 mb-3" role="alert">
                <small>
                  This email is already registered. <br />
                  <Link to="/login" className="fw-bold text-dark text-decoration-underline">
                    Click here to Log In
                  </Link>
                </small>
              </div>
            )}

            <button type="submit" className="btn d-block btn-primary-register">Sign Up</button>

            <button type="button" className="auth-google-btn" onClick={handleGoogleSignIn}>
              Sign in with Google
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}